import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { AppConfiguration } from "read-appsettings-json";
import "./jobspace-auth.css";

/*
  JobSpaceLogin — faithful React port of the Claude Design project
  "JobSpace Login.dc.html". Split-screen: sign-in form + green promo panel.
  Authentication is READ-ONLY: it queries the CIAMUsers collection (a GET via
  ciamapi action/get, same pattern as the legacy SignIn page) and, on a match,
  sets sessionStorage and routes to the logged-in experience. No DB writes.
*/

const Logo = () => (
  <div style={{ width: 30, height: 30, borderRadius: 9, background: "linear-gradient(145deg,#00b368,#008a4d)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 12px rgba(0,154,87,.32)" }}>
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none"><path d="M9 7V5.5A2.5 2.5 0 0 1 11.5 3h1A2.5 2.5 0 0 1 15 5.5V7m-9 0h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2Z" stroke="#fff" strokeWidth="2" strokeLinecap="round" /></svg>
  </div>
);

const GoogleIcon = () => (
  <svg width="19" height="19" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.5 12.2c0-.7-.1-1.4-.2-2H12v3.8h5.9a5 5 0 0 1-2.2 3.3v2.7h3.6c2.1-2 3.2-4.9 3.2-7.8Z" /><path fill="#34A853" d="M12 23c2.9 0 5.4-1 7.2-2.7l-3.6-2.7c-1 .7-2.3 1.1-3.6 1.1-2.8 0-5.1-1.9-6-4.4H2.3v2.8A11 11 0 0 0 12 23Z" /><path fill="#FBBC05" d="M6 14.3a6.6 6.6 0 0 1 0-4.2V7.3H2.3a11 11 0 0 0 0 9.8L6 14.3Z" /><path fill="#EA4335" d="M12 5.5c1.6 0 3 .5 4.1 1.6l3.1-3.1A11 11 0 0 0 2.3 7.3L6 10.1c.9-2.6 3.2-4.6 6-4.6Z" /></svg>
);

const input = { width: "100%", padding: "13px 16px", border: "1.5px solid #dde5df", borderRadius: 100, fontFamily: "inherit", fontSize: 15, outline: "none", background: "#fff" };
const label = { display: "block", fontSize: 13, fontWeight: 700, marginBottom: 7 };

export default function JobSpaceLogin() {
  const navigate = useNavigate();
  const [userId, setUserId] = useState("");
  const [password, setPassword] = useState("");
  const [isEmployer, setIsEmployer] = useState(false);
  const [companyCode, setCompanyCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const signIn = async (e) => {
    if (e) e.preventDefault();
    if (!userId.trim() || !password) { setError("Please enter your User ID and password."); return; }
    if (isEmployer && !companyCode.trim()) { setError("Please enter your Company Code."); return; }
    setError("");
    setLoading(true);
    // start from a clean session, mirroring the legacy login modal
    localStorage.clear();
    sessionStorage.clear();
    try {
      // Read-only credential check against CIAMUsers (mirrors NavBar's
      // handleGetCIAMUsers). Company code is only sent for employer sign-in.
      const filter_data = { isDeleted: "0", userid: userId.trim(), password };
      if (isEmployer) filter_data.companycode = companyCode.trim();
      const Params = { dbname: "CIAMDBDEVV", _collection: "CIAMUsers", filter_data };
      const res = await axios.post(AppConfiguration.Setting().ciamapi + "action/get", Params);
      const rows = (res.data && res.data.CIAMUsers) || [];
      const dataTmp = [];
      rows.forEach((itm) => {
        try {
          const strJson = "{" + itm.substring(46, itm.length);
          const headerId = itm.substring(18, itm.length).split("')")[0];
          const json = JSON.parse(strJson.replace(/'/g, '"'));
          json.headerId = headerId;
          dataTmp.push(json);
        } catch (err) { /* skip unparseable row */ }
      });
      if (dataTmp.length > 0) {
        // Keys the Account shell reads (userCollection[0].userid):
        sessionStorage.setItem("userCollection", JSON.stringify(dataTmp));
        sessionStorage.setItem("userCollectionArr", JSON.stringify(dataTmp));
        sessionStorage.setItem("userdata", JSON.stringify(dataTmp));
        sessionStorage.setItem("userdataArray", JSON.stringify(dataTmp));
        sessionStorage.setItem("dataListuser", JSON.stringify(dataTmp));
        // Per-key values the legacy NavBar modal stored (parity with CommonLayout pages):
        const u = dataTmp[0];
        const name = u.name && u.name.trim() !== "" ? u.name : `${u.firstName || ""} ${u.lastName || ""}`.trim();
        sessionStorage.setItem("userId", u.userid || "");
        sessionStorage.setItem("name", name);
        sessionStorage.setItem("email", u.email || "");
        sessionStorage.setItem("contact", u.contact || "");
        sessionStorage.setItem("role", u.role || "");
        sessionStorage.setItem("companyCode", u.companycode || "N/A");
        sessionStorage.setItem("address", u.address || "");
        navigate("/");
      } else {
        setError("Invalid credentials. Please try again.");
      }
    } catch (err) {
      setError("We couldn't sign you in right now. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="jsl-root" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", minHeight: "100vh", display: "flex", background: "#f4f7f4", color: "#16271f" }}>
      {/* form column */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", padding: "32px 40px" }}>
        <Link to="/" style={{ display: "flex", alignItems: "center", gap: 9, textDecoration: "none" }}>
          <Logo />
          <span style={{ fontWeight: 800, fontSize: 20, letterSpacing: "-.02em", color: "#16271f" }}>job<span style={{ color: "#009b57" }}>space</span></span>
        </Link>

        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <form onSubmit={signIn} className="jsl-stagger" style={{ width: "100%", maxWidth: 384 }}>
            <h1 style={{ margin: 0, fontSize: 30, fontWeight: 800, letterSpacing: "-.025em" }}>Welcome back</h1>
            <p style={{ margin: "9px 0 30px", fontSize: 15, color: "#6c7d74", fontWeight: 500 }}>Sign in to continue your job search.</p>

            <button type="button" className="jsa-google" style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, padding: 13, border: "1.5px solid #dde5df", borderRadius: 100, background: "#fff", fontFamily: "inherit", fontSize: 15, fontWeight: 600, color: "#2c3d34", cursor: "pointer" }}>
              <GoogleIcon />Continue with Google
            </button>

            <div style={{ display: "flex", alignItems: "center", gap: 14, margin: "22px 0" }}>
              <div style={{ flex: 1, height: 1, background: "#e2e9e3" }} />
              <span style={{ fontSize: 13, color: "#9aa89f", fontWeight: 600 }}>or</span>
              <div style={{ flex: 1, height: 1, background: "#e2e9e3" }} />
            </div>

            <label style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 16, padding: "11px 14px", border: "1.5px solid #e2e9e3", borderRadius: 14, background: "#fbfdfc", cursor: "pointer", fontSize: 13.5, fontWeight: 600, color: "#3c4d44" }}>
              <input type="checkbox" checked={isEmployer} onChange={(e) => setIsEmployer(e.target.checked)} style={{ width: 16, height: 16, accentColor: "#009b57" }} />
              Are you an Employer?
            </label>

            {isEmployer && (<>
              <label style={label}>Company Code</label>
              <input className="jsa-input" value={companyCode} onChange={(e) => setCompanyCode(e.target.value)} placeholder="Enter your company code" style={{ ...input, marginBottom: 16 }} />
            </>)}

            <label style={label}>User ID</label>
            <input className="jsa-input" value={userId} onChange={(e) => setUserId(e.target.value)} placeholder="Enter your User ID" style={{ ...input, marginBottom: 16 }} />

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 7 }}>
              <label style={{ fontSize: 13, fontWeight: 700 }}>Password</label>
              <Link to="/resetpassword" style={{ fontSize: 13, fontWeight: 600, color: "#009b57", textDecoration: "none" }}>Forgot password?</Link>
            </div>
            <input className="jsa-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" style={{ ...input, marginBottom: error ? 12 : 22 }} />

            {error && <div style={{ margin: "0 0 18px", fontSize: 13.5, fontWeight: 600, color: "#c0493e", background: "#fdf0ef", border: "1px solid #f3d3cf", borderRadius: 12, padding: "10px 14px" }}>{error}</div>}

            <button type="submit" disabled={loading} className="jsa-primary" style={{ display: "block", textAlign: "center", width: "100%", padding: 14, border: "none", borderRadius: 100, background: "#009b57", color: "#fff", fontFamily: "inherit", fontSize: 15, fontWeight: 700, cursor: loading ? "default" : "pointer", opacity: loading ? 0.7 : 1, boxShadow: "0 6px 16px rgba(0,154,87,.3)" }}>{loading ? "Signing in…" : "Sign in"}</button>

            <p style={{ margin: "24px 0 0", textAlign: "center", fontSize: 14, color: "#6c7d74", fontWeight: 500 }}>Don't have an account? <Link to="/signup" style={{ color: "#009b57", fontWeight: 700, textDecoration: "none" }}>Sign up free</Link></p>
          </form>
        </div>
      </div>

      {/* promo panel */}
      <div className="jsl-promo jsl-right" style={{ flex: 1, margin: "16px 16px 16px 0", borderRadius: 28, background: "linear-gradient(155deg,#0a8a4e,#066b3c)", position: "relative", overflow: "hidden", display: "flex", flexDirection: "column", justifyContent: "flex-end", padding: 48, color: "#fff" }}>
        <div style={{ position: "absolute", top: -80, right: -60, width: 280, height: 280, borderRadius: "50%", background: "rgba(255,255,255,.08)" }} />
        <div style={{ position: "absolute", top: 120, right: 140, width: 130, height: 130, borderRadius: "50%", background: "rgba(255,255,255,.06)" }} />
        <div style={{ position: "relative" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "7px 14px", borderRadius: 100, background: "rgba(255,255,255,.15)", fontSize: 13, fontWeight: 600, marginBottom: 28 }}>⭐ Trusted by 12,000+ Filipino companies</div>
          <h2 style={{ margin: 0, fontSize: 34, fontWeight: 800, lineHeight: 1.2, letterSpacing: "-.02em", maxWidth: 420 }}>Find a job that loves you back.</h2>
          <p style={{ margin: "16px 0 32px", fontSize: 16, lineHeight: 1.6, color: "rgba(255,255,255,.85)", maxWidth: 400 }}>Over 40,000 active listings from the Philippines' top employers — updated every day.</p>
          <div style={{ display: "flex", gap: 36 }}>
            {[["40k+", "Open roles"], ["12k+", "Companies"], ["2M+", "Jobseekers"]].map(([n, l]) => (
              <div key={l}>
                <div style={{ fontSize: 28, fontWeight: 800 }}>{n}</div>
                <div style={{ fontSize: 13.5, color: "rgba(255,255,255,.8)", fontWeight: 500 }}>{l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
